import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { HOUR_12_FORMAT, HOUR_24_FORMAT, HOUR_FORMAT } from 'app/shared/constant';
import { DateutilService } from 'app/shared/services/dateutil.service';
import {
    FlexDiagnosticWindow,
    FlexDiagnosticWindowRequest,
    FlexExtendSessionValue,
} from 'app/shared/models/flex-diagnostic-window';
import { MonitorDiagnosticsService } from 'app/shared/services/monitor-diagnostics.service';
import { TrackBy } from 'app/shared/utils/track-by';
import { isUnscheduledFlexDiagnosticWindow } from 'app/shared/utils/flex-diagnostic-window.util';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { Subscription } from 'rxjs';

const SNACKBAR_DURATION = 4000;
const WINDOW_LIMIT_DAYS = 3;

export interface DiagnosticWindowDialogData {
    customerId: number;
    locationId: number;
    locationName: string;
    nextExpectedDiagnosticSession: Date | null;
    window?: FlexDiagnosticWindow;
}

interface DiagnosticWindowDurationOption {
    value: FlexExtendSessionValue;
    labelKey: string;
}

@Component({
    selector: 'app-diagnostic-window-dialog',
    templateUrl: './diagnostic-window-dialog.component.html',
    styleUrls: ['./diagnostic-window-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticWindowDialogComponent implements OnInit, OnDestroy {
    public isLoading = true;
    public isSaving = false;
    public customerDateFormat: string;
    public locationName: string;
    public nextExpectedDiagnosticSession: Date | null;
    public minDate = new Date();
    public maxDate = new Date();
    public selectedDuration: FlexExtendSessionValue | null = null;
    public windowEnd: Date | null = null;

    public readonly durationOptions: DiagnosticWindowDurationOption[] = [
        { value: FlexExtendSessionValue.FifteenMinutes, labelKey: 'LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.DURATION_15' },
        { value: FlexExtendSessionValue.TwentyMinutes, labelKey: 'LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.DURATION_20' },
        { value: FlexExtendSessionValue.ThirtyMinutes, labelKey: 'LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.DURATION_30' },
    ];

    public translations = {
        saveSuccess: '',
        saveError: '',
        dismiss: '',
    };

    public trackByValue = TrackBy.byValue();

    private initialSelectedDuration: FlexExtendSessionValue | null = null;
    private initialWindowEndTimestamp: number | null = null;
    private subscriptions = new Array<Subscription>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DiagnosticWindowDialogData,
        public dialogRef: MatDialogRef<DiagnosticWindowDialogComponent>,
        private monitorDiagnosticsService: MonitorDiagnosticsService,
        private dateutilService: DateutilService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private uiUtils: UiUtilsService,
        private cdr: ChangeDetectorRef,
    ) {
        this.locationName = this.data.locationName;
        this.nextExpectedDiagnosticSession = this.data.nextExpectedDiagnosticSession;
        this.applyTranslations();
    }

    ngOnInit(): void {
        const dateFormat = this.dateutilService.getFormat();
        const is12HourFormat = this.dateutilService.timeFormat.getValue() !== HOUR_FORMAT;
        this.customerDateFormat = is12HourFormat ? `${dateFormat}, ${HOUR_12_FORMAT}` : `${dateFormat}, ${HOUR_24_FORMAT}`;

        this.minDate = new Date();
        this.maxDate = new Date();
        this.maxDate.setDate(this.maxDate.getDate() + WINDOW_LIMIT_DAYS);

        if (this.data.window) {
            this.applyDiagnosticWindow(this.data.window);
            this.initialSelectedDuration = this.selectedDuration;
            this.initialWindowEndTimestamp = this.windowEnd?.getTime() ?? null;
        }
        this.finishLoading();
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) sub.unsubscribe();
        this.subscriptions = [];
    }

    public get canSave(): boolean {
        if (this.selectedDuration === null || !this.windowEnd) return false;

        const endTime = this.windowEnd.getTime();
        const isExistingEndTime =
            this.initialWindowEndTimestamp !== null && endTime === this.initialWindowEndTimestamp;
        const isEndTimeValid = endTime >= this.minDate.getTime() && endTime <= this.maxDate.getTime();
        const hasChanges =
            this.selectedDuration !== this.initialSelectedDuration || endTime !== this.initialWindowEndTimestamp;

        return hasChanges && (isExistingEndTime || isEndTimeValid);
    }

    public windowEndChanged(event: MatDatepickerInputEvent<Date>): void {
        const value = event.value;

        if (!value || isNaN(value.getTime())) {
            this.windowEnd = null;
            return;
        }

        this.windowEnd = value;
    }

    public save(): void {
        const windowEnd = this.windowEnd;
        const selectedDuration = this.selectedDuration;
        if (!this.canSave || this.isSaving || !windowEnd || selectedDuration === null) return;

        const endTime = this.dateutilService.formatDateToUTC(windowEnd);
        if (!endTime) return;

        this.isSaving = true;

        const body: FlexDiagnosticWindowRequest = {
            extendSession: selectedDuration,
            endTime,
        };

        const saveSub = this.monitorDiagnosticsService
            .updateDiagnosticWindowFlex(this.data.customerId, this.data.locationId, body)
            .subscribe(
                () => {
                    this.snackBar.open(this.translations.saveSuccess, this.translations.dismiss, {
                        duration: SNACKBAR_DURATION,
                    });
                    this.dialogRef.close(true);
                },
                () => {
                    this.isSaving = false;
                    this.snackBar.open(this.translations.saveError, this.translations.dismiss, {
                        panelClass: 'custom-error-snack-bar',
                        duration: SNACKBAR_DURATION,
                    });
                    this.uiUtils.safeChangeDetection(this.cdr);
                },
            );

        this.subscriptions.push(saveSub);
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    private applyDiagnosticWindow(result: FlexDiagnosticWindow): void {
        if (!result) return;

        if (isUnscheduledFlexDiagnosticWindow(result)) return;

        const durationOption = this.durationOptions.find((option) => option.value === result.extendSession);
        if (durationOption) {
            this.selectedDuration = durationOption.value;
        }

        if (result.endTime) {
            this.windowEnd = new Date(result.endTime);
        }
    }

    private finishLoading(): void {
        this.isLoading = false;
        this.uiUtils.safeChangeDetection(this.cdr);
    }

    private applyTranslations(): void {
        const translateSub = this.translate
            .get([
                'LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.SAVE_SUCCESS',
                'LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.SAVE_ERROR',
                'COMMON.DISMISS_TEXT',
            ])
            .subscribe((translateValues) => {
                this.translations.saveSuccess = translateValues['LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.SAVE_SUCCESS'];
                this.translations.saveError = translateValues['LOCATION_DASHBOARD.DIAGNOSTIC_WINDOW.SAVE_ERROR'];
                this.translations.dismiss = translateValues['COMMON.DISMISS_TEXT'];
            });

        this.subscriptions.push(translateSub);
    }
}
